import React from "react";
import { createInertiaApp } from "@inertiajs/react";
import { StrictMode } from "react";
import { createRoot, hydrateRoot } from "react-dom/client";
import { createGlobalStyle } from "styled-components";

import { GlobalCSS } from "../styles/global-css";

createInertiaApp({
  // Set default page title
  // see https://inertia-rails.dev/guide/title-and-meta
  //
  // title: title => title ? `${title} - App` : 'App',

  // Disable progress bar
  //
  // see https://inertia-rails.dev/guide/progress-indicators
  // progress: false,

  resolve: (name) => {
    // Scope the glob — a bare "../pages/**" eager-bundles EVERY Inertia page in the app (the whole
    // frontdesk_v2 staff app, HGS, inertia_example) into this public marketing chunk. The "hebe"
    // layout only renders hebe/{home,insights,privacy_policy,terms_of_service}; hebe/hgs/** is
    // excluded because Hebe::Hgs::ApplicationController overrides the layout to "guests", so those
    // pages are resolved by guests.jsx, never here.
    const pages = import.meta.glob(
      ["../pages/hebe/**/*.jsx", "!../pages/hebe/hgs/**"],
      { eager: true },
    );
    const page = pages[`../pages/${name}.jsx`];
    if (!page) {
      console.error(`Missing Inertia page component: '${name}.jsx'`);
    }

    // To use a default layout, import the Layout component
    // and use the following lines.
    // see https://inertia-rails.dev/guide/pages#default-layouts
    //
    // page.default.layout ||= (page) => (<Layout>{page}</Layout>)

    return page;
  },

  setup({ el, App, props }) {
    const GlobalStyle = createGlobalStyle(GlobalCSS);

    let ret = (
      <React.Fragment>
        <GlobalStyle />
        <App {...props} />
      </React.Fragment>
    );

    const tree = <StrictMode>{ret}</StrictMode>;

    // The marketing pages are server-rendered (see ssr.jsx). Hydrating adopts that
    // markup; createRoot would throw all of it away and rebuild the DOM, which
    // costs the visitor a second render and hides any server/client divergence
    // because React never compares the two. Every other surface has no server
    // markup to adopt and takes a fresh root.
    if (el.hasAttribute("data-server-rendered")) {
      hydrateRoot(el, tree);
    } else {
      createRoot(el).render(tree);
    }
  },

  defaults: {
    form: {
      forceIndicesArrayFormatInFormData: false,
    },
    future: {
      useScriptElementForInitialPage: true,
      useDataInertiaHeadAttribute: true,
      useDialogForErrorModal: true,
      preserveEqualProps: true,
    },
  },
}).catch((error) => {
  // This ensures this entrypoint is only loaded on Inertia pages
  // by checking for the presence of the root element (#app by default).
  // Feel free to remove this `catch` if you don't need it.
  if (document.getElementById("app")) {
    throw error;
  } else {
    console.error(
      "Missing root element.\n\n" +
        "If you see this error, it probably means you loaded Inertia.js on non-Inertia pages.\n" +
        'Consider moving <%= vite_javascript_tag "inertia.jsx" %> to the Inertia-specific layout instead.',
    );
  }
});
